import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import { ColorPaletteSelectorComponent, ColorSwatchSelectorComponent } from '@mode/colors/ui';
import { FlamingoTypes, OrgTypes } from '@mode/shared/contract-common';
import { ICON_FIELD_MAP, getFieldIcon } from '@mode/shared/util-js';
import { CompiledField } from '@mode/shared/util-switch';
import { ColorPalette } from 'libs/shared/contract-common/src/lib/account/org.types';
import { partition } from 'lodash';
import * as FieldHelpers from '../../helpers/field-helpers';
import { LabeledFieldListMenuComponent, MenuItem } from '../../menu/labeled-field-list-menu.component';

export interface CompiledFieldMenuItem extends MenuItem {
  field: CompiledField;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    MatLegacyMenuModule,
    ColorSwatchSelectorComponent,
    LabeledFieldListMenuComponent,
    ColorPaletteSelectorComponent,
  ],
  selector: 'mode-field-and-palette-selector',
  templateUrl: './field-and-palette-selector.component.html',
  styleUrls: ['./field-and-palette-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldAndPaletteSelectorComponent implements OnChanges {
  @Input() view!: FlamingoTypes.ModeView;
  @Input() activeColorPalette: OrgTypes.ColorPalette | undefined = undefined;
  @Input() allColorPalettes: OrgTypes.ColorPalette[] = [];
  @Input() currentField: CompiledField | undefined = undefined;
  @Input() allFields: CompiledField[] = [];
  @Output() paletteChanged = new EventEmitter<OrgTypes.ColorPalette>();
  @Output() fieldChanged = new EventEmitter<CompiledField>();

  selectedField: CompiledFieldMenuItem | undefined; // currentField -> selectedField
  otherFields: CompiledFieldMenuItem[] = []; // allFields -> otherFields

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allColorPalettes']?.currentValue) {
      // Partition the list into custom and predefined
      const [customPalettes, predefinedPalettes] = partition(
        changes['allColorPalettes'].currentValue,
        (palette: ColorPalette) => !palette.predefined
      );
    }

    // creates MenuItem[] based on all fields
    if (changes['allFields']?.currentValue) {
      this.otherFields = changes['allFields'].currentValue.map((field: CompiledField) =>
        this.makeMenuItemFromCompiledField(field)
      );
    }
    // Find the menu item associated with the current field
    this.selectedField =
      this.otherFields.find((menuItem) => menuItem.field.formula === this.currentField?.formula) ?? this.otherFields[0];
  }

  onFieldChanged(data: CompiledFieldMenuItem) {
    if (this.selectedField !== data) {
      this.fieldChanged.emit(data.field);
    }
  }

  onPaletteChanged(palette: OrgTypes.ColorPalette) {
    this.paletteChanged.emit(palette);
  }

  private makeMenuItemFromCompiledField(compiledField: CompiledField): CompiledFieldMenuItem {
    return {
      value: {
        isContinuous: compiledField.isContinuous,
        isDiscrete: compiledField.isDiscrete,
      },
      field: compiledField,
      name: compiledField.hasAnalyticFunc
        ? `${FieldHelpers.getDescriptionExprForm(compiledField, false).trimStart()} - ${compiledField.displayFormula}`
        : compiledField.displayFormula,
      icon: compiledField.hasAnalyticFunc
        ? ICON_FIELD_MAP.TABLECAL
        : getFieldIcon(compiledField.formulaDataType, this.formulaHasCalcAttr(compiledField.formula)),
    };
  }

  private formulaHasCalcAttr(encFieldFormula: string): boolean {
    return !!CompiledField.formulaHasCalcAttr(this.view, encFieldFormula);
  }
}
